import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';


const Izin = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('izin');

  // State untuk modal
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedJenisIzin, setSelectedJenisIzin] = useState(null);
  const [keterangan, setKeterangan] = useState('');
  const [jenisIzinList, setJenisIzinList] = useState([]);
  const [jumlahHari, setJumlahHari] = useState(1);
  const [ijinType, setIjinType] = useState('');
  const [ijinOptions, setIjinOptions] = useState([]);

  const filteredJenisIzin = useMemo(() => {
    return jenisIzinList.filter((item) => {
      const nama =
        typeof item === "string"
          ? item
          : item?.nama || "";

      return nama.toLowerCase();
    });
  }, [jenisIzinList]);


  // Refs untuk tanda tangan
  const canvasRef = useRef(null);
  const tomSelectRef = useRef(null); // nanti bisa diisi dengan TomSelect jika dipakai
  const isDrawing = useRef(false);
  const ctxRef = useRef(null);

  // Load data jenis izin dari localStorage saat mount
  useEffect(() => {
    const data = localStorage.getItem('jenis_izin_list');
    if (data) {
      try {
        const parsed = JSON.parse(data);
        setJenisIzinList(Array.isArray(parsed) ? parsed : []);
      } catch (e) {
        console.error('Error parsing jenis_ijin_list', e);
        setJenisIzinList([]);
      }
    }
  }, []);

  // Setup canvas untuk tanda tangan
  useEffect(() => {
    if (canvasRef.current) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      ctx.lineCap = 'round';
      ctx.strokeStyle = '#333';
      ctx.lineWidth = 3;
      ctxRef.current = ctx;

      // Responsif canvas
      const resizeCanvas = () => {
        const { width } = canvas.getBoundingClientRect();
        canvas.width = width * window.devicePixelRatio;
        canvas.height = 150 * window.devicePixelRatio;
        ctx.scale(window.devicePixelRatio, window.devicePixelRatio);
      };
      resizeCanvas();
      window.addEventListener('resize', resizeCanvas);
      return () => window.removeEventListener('resize', resizeCanvas);
    }
  }, [isModalOpen]);

  const startDrawing = (e, isTouch = false) => {
    isDrawing.current = true;
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const x = (isTouch ? e.touches[0].clientX : e.clientX) - rect.left;
    const y = (isTouch ? e.touches[0].clientY : e.clientY) - rect.top;
    ctxRef.current.beginPath();
    ctxRef.current.moveTo(x, y);
  };

  // Tambahkan useEffect baru ini (letakkan di bawah useEffect canvas setup)
  useEffect(() => {
    if (isModalOpen && canvasRef.current) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
      }
    }
  }, [isModalOpen]);

  const draw = (e, isTouch = false) => {
    if (!isDrawing.current) return;
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const x = (isTouch ? e.touches[0].clientX : e.clientX) - rect.left;
    const y = (isTouch ? e.touches[0].clientY : e.clientY) - rect.top;
    ctxRef.current.lineTo(x, y);
    ctxRef.current.stroke();
  };

  const stopDrawing = () => {
    isDrawing.current = false;
  };

  const clearSignature = () => {
    if (!canvasRef.current) return; // Tambahkan pengecekan ini
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
    }
  };

  const openModal = (jenis) => {
    setSelectedJenisIzin(jenis);
    setKeterangan('');
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedJenisIzin(null);
    setKeterangan('');
  };

  const tabs = [
    { key: 'izin', label: 'Izin / Cuti' },
    { key: 'sakit', label: 'Sakit' },
    { key: 'dinas', label: 'Dinas' },
  ];

  const cardStyle = {
    backgroundColor: '#fff',
    borderRadius: 24,
    padding: '24px 20px',
    width: '100%',
    maxWidth: 450,
    alignSelf: 'center',
    boxShadow: '0 4px 15px rgba(0, 0, 0, 0.05)',
    border: '1px solid #cbd5e1',
    boxSizing: 'border-box',
    margin: '0 auto 24px',
  };

  return (
    <div
      style={{
        // position: 'fixed',
        inset: 0,
        background: 'linear-gradient(180deg, #F5F9FF 0%, #E8F1FF 40%, #FFFFFF 100%)',
        display: 'flex',
        justifyContent: 'center',
        flexDirection: 'column',
        fontFamily: 'Poppins, sans-serif',
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <div style={{ position: 'relative', height: 80, flexShrink: 0, maxWidth: 450, width: '100%', margin: '0 auto', boxSizing: 'border-box' }}>
        <motion.button
          onClick={() => navigate('/')}
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          whileTap={{ scale: 0.9 }}
          style={{
            position: 'absolute',
            top: 20,
            left: 20,
            width: 40,
            height: 40,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: '#fff',
            borderRadius: '50%',
            boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
            cursor: 'pointer',
            border: 'none',
            zIndex: 10,
          }}
          aria-label="Kembali"
        >
          <ArrowLeft size={22} color="#333" />
        </motion.button>

        <div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          style={{
            position: 'absolute',
            top: 20,
            right: 20,
            backgroundColor: '#fff',
            borderRadius: 30,
            boxShadow: '0 4px 15px rgba(0, 0, 0, 0.05)',
            border: '1px solid #cbd5e1',
            display: 'flex',
            overflow: 'hidden',
            zIndex: 20,
          }}
        >
          {tabs.map((tab, idx) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              style={{
                padding: '10px 18px',
                fontSize: 17,
                height: 39,
                fontWeight: 600,
                color: activeTab === tab.key ? '#2563eb' : '#666',
                backgroundColor: activeTab === tab.key ? '#eff6ff' : 'transparent',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                border: 'none',
                justifyContent: 'center',
                display: 'flex',
                borderRight: idx < tabs.length - 1 ? '1px solid #cbd5e1' : 'none',
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div style={{
        flex: 1,
        overflowY: 'auto',
        padding: '20px',
        display: 'flex',
        marginTop: -10,
        flexDirection: 'column',
        alignItems: 'center',
      }}>
        <div
          key={activeTab}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          style={cardStyle}
        >
          {activeTab === 'izin' && (
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 60, marginBottom: 24 }}>📝</div>
              <h3 style={{ fontWeight: 700, fontSize: 20, color: '#111827', marginBottom: 16 }}>
                Pengajuan Izin / Cuti
              </h3>
              <p style={{ color: '#666', fontSize: 15, lineHeight: '1.6', marginBottom: 24 }}>
                Pilih jenis izin atau cuti yang ingin diajukan di bawah ini.
              </p>

              {jenisIzinList.length === 0 ? (
                <p style={{ color: '#888', fontSize: 15 }}>
                  Tidak ada data jenis izin. (Simpan di localStorage dengan key: jenis_ijin_list)
                </p>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 12, marginTop: 20 }}>
                  {filteredJenisIzin.map((jenis, index) => {
                    const nama = typeof jenis === "string" ? jenis : jenis.nama;
                    const keteranganAsli =
                      typeof jenis === "object" ? jenis.keterangan : "";
                    const keterangan =
                      String(keteranganAsli || "").trim() ||
                      (String(nama || "").trim().toLowerCase() === "izin" ? "Mendesak" : "");
                    const namaTampil =
                      String(nama || "").trim().toLowerCase() === "keluarga"
                        ? "Keluarga kandung, mertua"
                        : nama;
                    const keteranganTampil = /^keluarga\b/i.test(String(keterangan || "").trim())
                      ? String(keterangan).trim().replace(/^keluarga\b/i, "Keluarga kandung, mertua")
                      : String(keterangan || "").trim().toLowerCase() === "mendadak"
                        ? "Mendesak"
                      : keterangan;
                    const kategoriCuti = String(keterangan || nama || "").trim().toLowerCase();
                    const memotongCutiTahunan = [
                      "mendesak",
                      "mendadak",
                      "setengah hari",
                      "tahunan",
                    ].some(kategori => kategoriCuti.includes(kategori));

                    return (
                      <div
                        key={index}
                        onClick={() => {
                          navigate("/izin/form", {
                            state: {
                              jenis,
                            }
                          });
                        }}

                        style={{
                          padding: "12px 16px",
                          borderRadius: 16,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: 12,
                          cursor: "pointer",

                          background: "#ffffff",
                          border: "1px solid #e3eef7",
                          boxShadow: "0 2px 6px rgba(0,0,0,0.05)",
                          transition: "all .25s ease",
                        }}
                        onMouseDown={(e) => {
                          e.currentTarget.style.background = "#f0f7ff";
                          e.currentTarget.style.boxShadow = "0 4px 10px rgba(0,123,209,.18)";
                        }}
                        onMouseUp={(e) => {
                          e.currentTarget.style.background = "#ffffff";
                          e.currentTarget.style.boxShadow = "0 2px 6px rgba(0,0,0,0.05)";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = "#ffffff";
                          e.currentTarget.style.boxShadow = "0 2px 6px rgba(0,0,0,0.05)";
                        }}
                      >
                        {/* ICON KIRI */}
                        <div
                          style={{
                            width: 36,
                            height: 36,
                            borderRadius: 10,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            background: "#eaf5ff",
                            fontSize: 16,
                            flexShrink: 0,
                          }}
                        >
                          📝
                        </div>

                        {/* TEXT (RATA KIRI) */}
                        <div
                          style={{
                            flex: 1,
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "flex-start",
                            justifyContent: "center",
                            textAlign: "left",
                            lineHeight: 1.25,
                          }}
                        >
                          <span style={{ fontSize: 14, fontWeight: 700, color: "#222" }}>
                            {namaTampil || `Izin ${index + 1}`}
                          </span>

                          {keteranganTampil && (
                            <span
                              style={{
                                fontSize: 12,
                                fontWeight: 400,
                                color: "#64748b",
                                flex: 1,
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "flex-start",
                                marginTop: 2,
                              }}
                            >
                              {keteranganTampil}
                            </span>
                          )}
                          <span
                            style={{
                              fontSize: 11,
                              fontWeight: 400,
                              fontStyle: "italic",
                              color: memotongCutiTahunan ? "#b45309" : "#64748b",
                              marginTop: 3,
                            }}
                          >
                            {memotongCutiTahunan
                              ? "Memotong cuti tahunan"
                              : "Tidak memotong cuti tahunan"}
                          </span>
                        </div>

                        {/* ARROW KANAN */}
                        <div
                          style={{
                            width: 36,
                            height: 36,
                            borderRadius: 10,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            background: "#eaf5ff",
                            fontSize: 14,
                            flexShrink: 0,
                          }}
                        >
                          ↗
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {activeTab === 'sakit' && (
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 60, marginBottom: 24 }}>🤒</div>
              <h3 style={{ fontWeight: 700, fontSize: 20, color: '#111827', marginBottom: 16 }}>
                Pelaporan Izin Sakit
              </h3>
              <p style={{ color: '#666', fontSize: 15, lineHeight: '1.6', marginBottom: 24 }}>
                Laporkan sakit dan upload surat keterangan dokter di sini.
              </p>
              <div
                onClick={() => navigate('/sakit/form')}
                style={{
                  padding: '12px 16px',
                  borderRadius: 16,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 12,
                  cursor: 'pointer',
                  background: '#ffffff',
                  border: '1px solid #e3eef7',
                  boxShadow: '0 2px 6px rgba(0,0,0,0.05)',
                  transition: 'all .25s ease',
                }}
                onMouseDown={e => { e.currentTarget.style.background = '#f0f7ff'; e.currentTarget.style.boxShadow = '0 4px 10px rgba(0,123,209,.18)'; }}
                onMouseUp={e => { e.currentTarget.style.background = '#ffffff'; e.currentTarget.style.boxShadow = '0 2px 6px rgba(0,0,0,0.05)'; }}
                onMouseLeave={e => { e.currentTarget.style.background = '#ffffff'; e.currentTarget.style.boxShadow = '0 2px 6px rgba(0,0,0,0.05)'; }}
              >
                <div style={{ width: 36, height: 36, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#fef3c7', fontSize: 16, flexShrink: 0 }}>🤒</div>
                <div style={{ flex: 1, textAlign: 'left' }}>
                  <span style={{ fontSize: 14, fontWeight: 700, color: '#222' }}>Izin Sakit</span>
                  <br />
                  <span style={{ fontSize: 12, color: '#64748b' }}>Lapor sakit + upload surat dokter</span>
                  <br />
                  <span style={{ fontSize: 11, color: '#64748b', fontStyle: 'italic' }}>
                    Tidak memotong cuti tahunan jika ada surat dokter
                  </span>
                </div>
                <div style={{ width: 36, height: 36, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#fef3c7', fontSize: 14, flexShrink: 0 }}>↗</div>
              </div>
            </div>
          )}

          {activeTab === 'dinas' && (
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 60, marginBottom: 24 }}>🚗</div>
              <h3 style={{ fontWeight: 700, fontSize: 20, color: '#111827', marginBottom: 16 }}>
                Pelaporan Dinas Luar
              </h3>
              <p style={{ color: '#666', fontSize: 15, lineHeight: '1.6', marginBottom: 24 }}>
                Laporkan perjalanan dinas dan upload bukti perjalanan di sini.
              </p>

              <div
                onClick={() => navigate('/dinas/form')}
                style={{
                  padding: '12px 16px',
                  borderRadius: 16,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 12,
                  cursor: 'pointer',
                  background: '#ffffff',
                  border: '1px solid #e3eef7',
                  boxShadow: '0 2px 6px rgba(0,0,0,0.05)',
                  transition: 'all .25s ease',
                }}
                onMouseDown={e => { e.currentTarget.style.background = '#f0f7ff'; e.currentTarget.style.boxShadow = '0 4px 10px rgba(0,123,209,.18)'; }}
                onMouseUp={e => { e.currentTarget.style.background = '#ffffff'; e.currentTarget.style.boxShadow = '0 2px 6px rgba(0,0,0,0.05)'; }}
                onMouseLeave={e => { e.currentTarget.style.background = '#ffffff'; e.currentTarget.style.boxShadow = '0 2px 6px rgba(0,0,0,0.05)'; }}
              >
                <div style={{ width: 36, height: 36, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#e0e7ff', fontSize: 16, flexShrink: 0 }}>🚗</div>
                <div style={{ flex: 1, textAlign: 'left' }}>
                  <span style={{ fontSize: 14, fontWeight: 700, color: '#222' }}>Dinas Luar</span>
                  <br />
                  <span style={{ fontSize: 12, color: '#64748b' }}>Lapor dinas + upload bukti / foto</span>
                </div>
                <div style={{ width: 36, height: 36, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#e0e7ff', fontSize: 14, flexShrink: 0 }}>↗</div>
              </div>

            </div>
          )}
        </div>
      </div>

      {/* Modal Bottom Sheet */}
      <AnimatePresence>
        {isModalOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={{
              position: 'fixed',
              inset: 0,
              backgroundColor: 'rgba(0,0,0,0.4)',
              zIndex: 50,
            }}
            onClick={closeModal}
          >
            <motion.div
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ type: 'spring', damping: 30, stiffness: 300 }}
              onClick={(e) => e.stopPropagation()}
              style={{
                position: 'absolute',
                bottom: 0,
                left: 0,
                right: 0,
                backgroundColor: '#fff',
                borderTopLeftRadius: 24,
                borderTopRightRadius: 24,
                maxHeight: '90vh',
                overflowY: 'auto',
                boxShadow: '0 -4px 20px rgba(0,0,0,0.15)',
              }}
            >
              {/* Header Modal */}
              <div style={{ padding: '16px 20px', borderBottom: '1px solid #eee', position: 'relative' }}>
                <h3 style={{ fontSize: 18, fontWeight: 700, textAlign: 'center' }}>
                  Pengajuan: {selectedJenisIzin
                    ? (typeof selectedJenisIzin === 'string' ? selectedJenisIzin : selectedJenisIzin.nama + ' ' + selectedJenisIzin.keterangan)
                    : 'Izin'}
                </h3>
                <button
                  onClick={closeModal}
                  style={{
                    position: 'absolute',
                    right: 16,
                    top: 16,
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                  }}
                >
                  <X size={24} color="#666" />
                </button>
              </div>

              {/* Form */}
              <div style={{ padding: '24px 20px' }}>
                <div style={{ marginBottom: 16 }}>
                  <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>Keterangan:</label>
                  <textarea
                    value={keterangan}
                    onChange={(e) => setKeterangan(e.target.value)}
                    placeholder="Contoh: Sakit demam tinggi, istirahat 2 hari"
                    rows={4}
                    style={{
                      width: '100%',
                      padding: '12px',
                      borderRadius: 12,
                      border: '1.5px solid #ddd',
                      fontSize: 15,
                      backgroundColor: '#fff',
                      resize: 'none',
                      outline: 'none',
                      boxSizing: 'border-box',
                    }}
                  />
                </div>

                <div style={{ marginBottom: 16 }}>
                  <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
                    Tugas Digantikan Oleh:
                  </label>
                  <select
                    ref={tomSelectRef}
                    style={{
                      width: '100%',
                      padding: '12px',
                      borderRadius: 12,
                      border: '1.5px solid #ddd',
                      fontSize: 15,
                      backgroundColor: '#fff',
                    }}
                  >
                    <option value="">Pilih karyawan pengganti</option>
                    {/* Diisi data karyawan nanti */}
                    <option value="1">Ahmad</option>
                    <option value="2">Siti</option>
                    <option value="3">Budi</option>
                  </select>
                </div>

                <div style={{ marginBottom: 20 }}>
                  <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>Tanda Tangan:</label>
                  <canvas
                    ref={canvasRef}
                    style={{
                      border: '1.5px dashed #ccc',
                      borderRadius: 12,
                      width: '100%',
                      height: 150,
                      backgroundColor: '#f9f9f9',
                      touchAction: 'none',
                    }}
                    onMouseDown={startDrawing}
                    onMouseMove={draw}
                    onMouseUp={stopDrawing}
                    onMouseLeave={stopDrawing}
                    onTouchStart={(e) => { e.preventDefault(); startDrawing(e, true); }}
                    onTouchMove={(e) => { e.preventDefault(); draw(e, true); }}
                    onTouchEnd={stopDrawing}
                  />
                  <button
                    onClick={clearSignature}
                    style={{
                      marginTop: 8,
                      color: '#dc2626',
                      background: 'none',
                      border: 'none',
                      fontSize: 13,
                      cursor: 'pointer',
                    }}
                  >
                    Hapus Tanda Tangan
                  </button>
                </div>

                <button
                  style={{
                    width: '100%',
                    padding: '14px',
                    backgroundColor: '#2563eb',
                    color: '#fff',
                    fontSize: 16,
                    fontWeight: 600,
                    border: 'none',
                    borderRadius: 16,
                    cursor: 'pointer',
                  }}
                  onClick={() => {
                    alert('Pengajuan dikirim! (Implementasikan submit logic di sini)');
                    closeModal();
                  }}
                >
                  Kirim Pengajuan
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Izin;
